import { GeoJSONFeatureCollection } from '@/components/maps/map.types';
import { Body4 } from '@/components/typography';
import { H3, HTable } from '@/components/typography/heading/headings.component';
import { Table, TableBody, TableCellSmall, TableHeader, TableHeadSmall, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

type FarmMapProps = {
  data: GeoJSONFeatureCollection;
  customerId?: string;
  projectId?: string;
  farmId?: string;
  fieldId?: string;
  className?: string;
};

export const FarmMapTable = ({ customerId, projectId, farmId, fieldId, data, className }: FarmMapProps) => {
  const navigate = useNavigate();
  const href = customerId && projectId && farmId ? `customers/${customerId}/projects/${projectId}/farms/${farmId}` : '';
  // fixed table height when the table becomes scrollable after having more than 8 fields/rows
  const tableHeightStyle = data && data.features?.length > 8 ? 'h-[300px]' : 'h-auto';

  return (
    <div className={cn('overflow-y-auto border border-neutral-200 rounded-lg', className, tableHeightStyle)}>
      <Table className="min-w-full">
        <TableHeader className="sticky-table-header">
          <TableRow>
            <TableHeadSmall>Field Number</TableHeadSmall>
            <TableHeadSmall>Field Name</TableHeadSmall>
            <TableHeadSmall>Agricarbon Field ID</TableHeadSmall>
          </TableRow>
        </TableHeader>
        <TableBody className="border-t-2">
          {data && data.features?.length ? (
            data.features.map((feature, index) => {
              const isUnmatched = !feature.properties.field_num;
              return (
                <TableRow
                  key={index}
                  onClick={() => {
                    if (!isUnmatched && !fieldId) {
                      navigate(`/${href}/fields/${feature.properties.agri_field_id}`);
                    }
                  }}
                  className={cn(!isUnmatched && !fieldId ? 'cursor-pointer hover:bg-neutral-100 transition-colors' : '')}
                >
                  <TableCellSmall>{feature.properties.field_num}</TableCellSmall>
                  <TableCellSmall>{feature.properties.field_id}</TableCellSmall>
                  <TableCellSmall>{feature.properties.agri_field_id}</TableCellSmall>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCellSmall colSpan={3} className="text-center">
                No field data available
              </TableCellSmall>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export const CarbonStockKeyTable = () => (
  <div className="w-full">
    <Table>
      <HTable className={cn('text-brand my-1')}>
        <H3 className="text-center">Carbon Stock</H3>
      </HTable>
      <Body4 className={cn('text-center mb-2')}>High, Medium or Low within your farm</Body4>
      <TableBody className="border-t-2">
        <TableRow className={cn('flex flex-row justify-between mx-4')}>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-green')} />
            High
          </TableCellSmall>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-yellow')} />
            Medium
          </TableCellSmall>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-red')} />
            Low
          </TableCellSmall>
          <TableCellSmall className={cn('flex flex-row items-center gap-2')}>
            <div className={cn('w-3 h-3 bg-grey')} />
            Not yet sampled
          </TableCellSmall>
        </TableRow>
      </TableBody>
    </Table>
  </div>
);
